/**
 * Shared archive/delete project mutations.
 *
 * Both the projects list (-projects.lazy.tsx) and the project settings
 * Danger Zone tab expose the same lifecycle actions; this hook owns the
 * mutation functions, success/error toasts, and error logging so the two
 * surfaces cannot drift apart. Callers only provide what genuinely differs:
 * the cache/navigation side effects to run on success.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { logError } from '@/lib/logger';
import { inboundRoutingKeys } from '@/lib/query-options';

interface ProjectLifecycleOptions {
  onArchiveSuccess?: (projectId: string) => void;
  onDeleteSuccess?: (projectId: string) => void;
}

export function useProjectLifecycleMutations({
  onArchiveSuccess,
  onDeleteSuccess,
}: ProjectLifecycleOptions = {}) {
  const { t } = useTranslation(['projects', 'common']);
  const { apiClient } = useApiClient();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  /**
   * Судьба проекта меняет то, что обязан говорить список скриптов (#498).
   *
   * Архивация выводит проект из активных, и каждое назначение в нём
   * перестаёт быть действующим — назначенные туда скрипты должны получить
   * маркер «Не назначен». Удаление каскадом сносит сами строки
   * `project_scripts`. Без этого список ещё пять минут (`staleTime`, без
   * рефетча по фокусу) показывает бейдж проекта, которого больше нет, и
   * молчит там, где должен предупреждать.
   *
   * По ПРЕФИКСУ `['scripts']`, а не по точному ключу с `selectedOrgId`: хук
   * зовут два экрана, и тянуть сюда контекст организации ради одной строки
   * значило бы навязать его каждому, кто их рендерит (тест списка проектов
   * на этом и упал). Префикс накрывает нужный кеш и заодно чужие вкладки
   * той же сессии, а лишний рефетч списка скриптов ничего не стоит.
   */
  const invalidateScriptsList = () =>
    queryClient.invalidateQueries({ queryKey: ['scripts'] });

  /**
   * Правила входящей маршрутизации тоже зависят от судьбы проекта (#1672):
   * чип маршрутизации в /analysis-setup считает включённые правила только в
   * АКТИВНЫХ проектах (`meta.enabledTotal`), и список живёт со `staleTime`
   * 5 минут. Без инвалидации архивный проект ещё пять минут числился бы
   * получателем. Префикс по тем же причинам, что и у скриптов выше.
   */
  const invalidateInboundRouting = () =>
    queryClient.invalidateQueries({ queryKey: inboundRoutingKeys.root });

  const archiveMutation = useMutation({
    mutationFn: (projectId: string) => apiClient.projects.archiveProject(projectId),
    onSuccess: (_, projectId) => {
      toast.success(
        t('projects.success.projectArchived'),
        t('projects.success.projectArchivedMessage')
      );
      void invalidateScriptsList();
      void invalidateInboundRouting();
      onArchiveSuccess?.(projectId);
    },
    onError: (err, projectId) => {
      logError(
        'project_lifecycle.archive_failed',
        { projectId },
        err instanceof Error ? err : undefined
      );

      let errorMessage = t('common:common.errorUnknown');
      if (err instanceof Error && 'data' in err) {
        const apiError = err as Error & { data?: { error?: string } };
        errorMessage = apiError.data?.error ?? err.message;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      toast.error(t('projects.error.failedToArchive'), errorMessage);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (projectId: string) => {
      const response = await apiClient.projects.deleteProject(projectId);
      if (!response || !response.success) {
        throw new Error(t('projects.error.failedToDeleteApiResponse'));
      }
      return response;
    },
    onSuccess: (_, projectId) => {
      toast.success(
        t('projects.success.projectDeleted'),
        t('projects.success.projectDeletedMessage')
      );
      void invalidateScriptsList();
      void invalidateInboundRouting();
      onDeleteSuccess?.(projectId);
    },
    onError: (err, projectId) => {
      logError(
        'project_lifecycle.delete_failed',
        { projectId },
        err instanceof Error ? err : undefined
      );
      toast.error(
        t('projects.error.failedToDelete'),
        err instanceof Error ? err.message : ''
      );
    },
  });

  return { archiveMutation, deleteMutation };
}
